import { React, useState } from "react"
import { useFetchProblems } from "../hooks/DataFetching/useFetchProblems"
import ProblemModal from "../components/ProblemModel"
import { Card, CardContent } from "./ui/card"
import { Badge } from "./ui/badge"
import { AlertCircle, Calendar, MessageSquare, Users, ArrowUp } from "lucide-react"
import { Button } from "./ui/button"

export const FetchProblems = ({ groupId, refreshKey = 0 }) => {
  const { problems, loading, error } = useFetchProblems(groupId, refreshKey)
  const [selectedProblem, setSelectedProblem] = useState(null)
  const [filter, setFilter] = useState("all") // all, solved, unsolved

  const handleProblemClick = (problem) => {
    setSelectedProblem(problem)
  }

  const closeModal = () => {
    setSelectedProblem(null)
  }

  // Format date to be more readable
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  // Filter problems based on selected filter
  const filteredProblems = () => {
    if (filter === "all") return problems
    if (filter === "solved") return problems.filter((p) => p.isSolved)
    if (filter === "unsolved") return problems.filter((p) => !p.isSolved)
    return problems
  }

  // Get the count of ideas associated with a problem
  const getIdeasCount = (problem) => {
    return problem.associatedIdeasCount || 0
  }

  // Get the count of votes for a problem
  const getVotesCount = (problem) => {
    return problem.votesCount || 0
  }

  const contents = loading ? (
    <div className="group-inline-state">
      <div className="group-task-spinner"></div>
      <span>Loading needs...</span>
    </div>
  ) : error ? (
    <div className="group-error-state">
      <AlertCircle className="mx-auto h-8 w-8 mb-2" />
      <p>Error loading problems: {error}</p>
    </div>
  ) : (
    <div className="group-list-stack">
      <div className="group-filter-row">
        <Button className={filter === "all" ? "group-filter-chip is-active" : "group-filter-chip"} variant="ghost" size="sm" onClick={() => setFilter("all")}>
          All
        </Button>
        <Button className={filter === "unsolved" ? "group-filter-chip is-active" : "group-filter-chip"} variant="ghost" size="sm" onClick={() => setFilter("unsolved")}>
          Unsolved
        </Button>
        <Button className={filter === "solved" ? "group-filter-chip is-active" : "group-filter-chip"} variant="ghost" size="sm" onClick={() => setFilter("solved")}>
          Solved
        </Button>
      </div>

      {filteredProblems().length === 0 ? (
        <div className="group-empty-state">
          <p>No problems found for this group.</p>
        </div>
      ) : (
        filteredProblems().map((problem) => (
          <Card
            key={problem.id}
            className="group-record-card"
            onClick={() => handleProblemClick(problem)}
          >
            <CardContent className="p-0">
              <div className="group-record-card-content">
                <div className="flex-1">
                  <div className="group-record-title-row">
                    <h3>{problem.title}</h3>
                    {problem.isSolved && (
                      <Badge className="group-status-badge is-success">Solved</Badge>
                    )}
                    {problem.isUrgent && !problem.isSolved && (
                      <Badge className="group-status-badge is-danger">Urgent</Badge>
                    )}
                  </div>

                  <p className="group-record-description">{problem.description}</p>

                  <div className="group-record-meta">
                    <span>
                      <Calendar className="h-3 w-3 mr-1" />
                      Posted: {formatDate(problem.createdOn)}
                    </span>
                    <span>
                      <Users className="h-3 w-3 mr-1" />
                      By: {problem.createdBy}
                    </span>
                    <span>
                      <MessageSquare className="h-3 w-3 mr-1" />
                      Ideas: {getIdeasCount(problem)}
                    </span>
                    <span>
                      <ArrowUp className="h-3 w-3 mr-1" />
                      Votes: {getVotesCount(problem)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  )

  return (
    <div className="problems-list group-nested-panel">
      <div className="group-section-heading">
        <span className="group-section-kicker"><AlertCircle className="h-4 w-4" /> Community signal</span>
        <h2>Problems & Needs</h2>
      </div>
      {contents}
      <ProblemModal isOpen={!!selectedProblem} onClose={closeModal} problemData={selectedProblem || {}} />
    </div>
  )
}

export default FetchProblems

